import { isAxiosError } from 'axios'

import type { YogiQLChartsService } from 'features/charts-v2/service/yogiql-service'
import type { Chart } from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type {
  CompileRefusal,
  FieldCatalogResponse,
} from 'features/charts-v2/types/yogiql'
import {
  YOGIQL_CHART_SPECS,
  type YogiQLChartName,
} from 'features/charts-v2/types/yogiql-chart-specs'
import { normalizeLegacyPanelSearch } from 'features/charts-v2/utils/normalize-legacy-panel-search'
import { toLegacyChart } from 'features/charts-v2/utils/to-legacy-chart'
import {
  type YogiQLNames,
  toYogiQLChartRequest,
} from 'features/charts-v2/utils/to-yogiql-chart-request'
import { warnYogiQLFallback } from 'features/charts-v2/utils/warn-yogiql-fallback'

/**
 * Run a panel through the YogiQL route and shape the result as a legacy
 * `Chart`. Returns null, after one warning, when the panel cannot be encoded
 * so the caller falls back to the legacy query. A compile refusal (422) is
 * warned with its detail and rethrown, so it surfaces as the query error.
 */
export async function runYogiQLChartQuery(
  service: YogiQLChartsService,
  hubUuid: string,
  panel: ChartPanel,
  catalog: FieldCatalogResponse,
  names: YogiQLNames,
): Promise<Chart | null> {
  const normalized = normalizeLegacyPanelSearch(panel)
  const encoded = toYogiQLChartRequest(normalized, catalog, names)
  if (!encoded.ok) {
    warnYogiQLFallback(
      `panel not encodable, using the legacy query: ${encoded.reason}`,
    )
    return null
  }
  let response
  try {
    response = await service.run(hubUuid, encoded.request)
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 422) {
      const detail = (error.response.data as { detail?: CompileRefusal[] })
        .detail
      const refusals = (detail ?? [])
        .map((r) => `${r.loc.slice(1).join('.')}: ${r.msg}`)
        .join('; ')
      warnYogiQLFallback(`compile refused: ${refusals || 'no detail'}`)
    }
    throw error
  }
  const spec = YOGIQL_CHART_SPECS[encoded.request.chart_type as YogiQLChartName]
  return toLegacyChart(response, encoded.request, normalized, spec, { names })
}
